import React, { cloneElement, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from 'components/Header';
import { ParsedService, ServiceRequestDetail, ServiceRequestSummary } from '../services/types';
import { getServiceRequestsByOrderId, updateStatus, StatusIds } from '../services/api';
import { formatApiErrorMessage, logApiError } from '../services/errors';
import { CUSTOMER_NAME_PLACEHOLDER } from '../services/const';
import type { UpdateServiceRequest, StatusId } from '../services/api';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import TagIcon from '@mui/icons-material/Tag';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhotoCameraBackOutlinedIcon from '@mui/icons-material/PhotoCameraBackOutlined';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
// import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BlockIcon from '@mui/icons-material/Block';
import { LoadingButton } from '@mui/lab';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import FormatQuoteOutlinedIcon from '@mui/icons-material/FormatQuoteOutlined';
import { getConsolidatedStatusLabel, toTitle } from 'services/util';
import { deriveHandoverTimestamp, extractAssociateInfo, formatOrderDateTime } from 'utils/orderMeta';
import { reorderEntriesWithSkuFirst } from 'utils/requiredInfo';
import { disableScrollRestore } from './StoreViewLandingPage';

interface StoreViewOrderDetailsPageProps {
  externalIdentity: string;
  associateId: string;
  associateEmail: string;
  associateName?: string;
  storeId: string;
}

const StoreViewOrderDetailsPage: React.FC<StoreViewOrderDetailsPageProps> = ({
  externalIdentity,
  associateId,
  associateEmail,
  associateName,
  storeId,
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();

  // `state` came from the card tap; prefer serviceItemId as primary identifier
  const summary = state as ServiceRequestSummary | undefined;
  const primaryId = (summary as any)?.serviceItemId || (state as any)?.serviceItemId || (summary as any)?.orderId;

  const [loading, setLoading] = useState(true);
  const [lineLoading, setLineLoading] = useState(false); // top linear bar during fetch
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ServiceRequestDetail | null>(null);
  const [parsedServices, setParsedServices] = useState<ParsedService[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [holdComment, setHoldComment] = useState('');
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [deliveryId, setDeliveryId] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState<'ADMIN_ERROR' | 'NO_PICKUP' | ''>('');
  const [cancelComment, setCancelComment] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [readyOverrideDialogOpen, setReadyOverrideDialogOpen] = useState(false);
  const [serviceActivityOpen, setServiceActivityOpen] = useState(false);
  // Order classification: SPECIAL_ORDER | STANDARD | QUICK | BOPIS
  type OrderClass = 'SPECIAL_ORDER' | 'STANDARD' | 'QUICK' | 'BOPIS';

  const getOrderType = () => (
    (
      (detail as any)?.order_type ||
      (detail as any)?.orderType ||
      (summary as any)?.order_type ||
      (summary as any)?.orderType ||
      (detail as any)?.product_type ||
      (summary as any)?.product_type ||
      (detail as any)?.productType ||
      (summary as any)?.productType ||
      ''
    )
      .toString()
      .toUpperCase()
  );

  const inferOrderClass = (): OrderClass => {
    const orderType = getOrderType();
    if (orderType === 'QUICK') return 'QUICK';
    if (orderType === 'BOPIS') return 'BOPIS';
    if (orderType === 'SPECIAL_ORDER') return 'SPECIAL_ORDER';
    return 'STANDARD';
  };

  const orderClass = inferOrderClass();
  const isCs1Store = storeId?.toUpperCase?.() === 'CS1';
  const orderTypeLabel = orderClass === 'SPECIAL_ORDER'
    ? 'Special Order'
    : orderClass === 'QUICK'
      ? 'Quick Service'
      : orderClass === 'BOPIS'
        ? 'BOPIS-BOSS'
        : 'Standard Service';
  const isBopis = orderClass === 'BOPIS';
  const showPhotoField = orderClass !== 'SPECIAL_ORDER' && Boolean(detail?.photoUrl);
  const rawLocation = ((detail as any)?.itemLocation || (detail as any)?.locationType || '')?.toString() || '';
  const locationLabel = rawLocation
    ? (rawLocation.includes('Kerrville') && rawLocation.includes('Store')
      ? 'Kerrville and Store'
      : rawLocation)
    : (isBopis ? 'Store' : '—');
  const locationLabelDisplay = useMemo(() => {
    if (!locationLabel) return locationLabel;
    return locationLabel
      .replace(/Kerrville/g, 'Product Care')
      .replace(/Store/g, 'In Store');
  }, [locationLabel]);
  const S = StatusIds;

  // Status-driven UI (prefer backend statusId when available)
  const parseStatusId = (value: number | string | undefined): StatusId | undefined => {
    if (value === undefined || value === null) return undefined;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? (numeric as StatusId) : undefined;
  };

  const rawStatus = (detail as any)?.statusId as number | string | undefined;
  const currentStatusId = parseStatusId(rawStatus);
  const isCompletedStatus = currentStatusId === S.COMPLETED;
  const isCancelledStatus = currentStatusId === S.CANCELLED;
  const orderStatusLabel = useMemo(
    () => String(getConsolidatedStatusLabel(currentStatusId)),
    [currentStatusId]
  );
  const statusIs = (id: StatusId) => currentStatusId === id;

  const allServices = parsedServices as any[];
  const storeServices = allServices.filter((s) => (s?.location_type || '').toString().includes('Store'));
  const kerrvilleServices = allServices.filter((s) => (s?.location_type || '').toString().includes('Kerrville'));

  // Allowed transitions map (from -> to)
  const ALLOWED_TRANSITIONS = useMemo(() => {
    const S = StatusIds;
    const pairs: Array<[StatusId, StatusId]> = [
      [S.OPEN, S.START],
      [S.OPEN, S.CANCELLED],
      [S.START, S.STOP],
      [S.STOP, S.WORK_IN_PROGRESS],
      [S.STOP, S.ON_HOLD],
      [S.STOP, S.CANCELLED],
      [S.WORK_IN_PROGRESS, S.SHIPPED_TO_KERRVILLE],
      [S.WORK_IN_PROGRESS, S.ON_HOLD],
      [S.WORK_IN_PROGRESS, S.CANCELLED],
      [S.ON_HOLD, S.CANCELLED],
      [S.SHIPPED_TO_KERRVILLE, S.RECEIVED_BY_KERRVILLE],
      [S.RECEIVED_BY_KERRVILLE, S.WORK_IN_PROGRESS_BY_KERRVILLE],
      [S.WORK_IN_PROGRESS_BY_KERRVILLE, S.SHIPPED_TO_STORE_BY_KERRVILLE],
      [S.SHIPPED_TO_STORE_BY_KERRVILLE, S.READY_TO_SEND],
      [S.READY_TO_SEND, S.READY_TO_PICKUP],
      [S.READY_TO_PICKUP, S.COMPLETED],
      [S.READY_TO_PICKUP, S.RETURN],
      [S.STOP, S.READY_TO_PICKUP],
      [S.OPEN, S.SHIPPED_TO_KERRVILLE],
      [S.READY_TO_PICKUP, S.ON_HOLD],
      [S.ON_HOLD, S.COMPLETED],
      [S.OPEN, S.WORK_IN_PROGRESS],
      [S.WORK_IN_PROGRESS, S.COMPLETED],
      [S.SHIPPED_TO_STORE_BY_KERRVILLE, S.READY_TO_PICKUP],
      [S.READY_TO_PICKUP, S.CANCELLED],
      [S.OPEN, S.COMPLETED],
      [S.SHIPPED_TO_KERRVILLE, S.READY_TO_PICKUP],
      [S.ON_HOLD, S.READY_TO_PICKUP],
      [S.RECEIVED_BY_KERRVILLE, S.READY_TO_PICKUP],
      [S.WORK_IN_PROGRESS_BY_KERRVILLE, S.READY_TO_PICKUP],
      [S.WORK_IN_PROGRESS, S.READY_TO_PICKUP],
    ];
    const set = new Set<string>();
    pairs.forEach(([from, to]) => set.add(`${from}->${to}`));
    return set;
  }, []);

  const canTransition = (from: StatusId | undefined, to: StatusId) => {
    if (from === undefined) return false;
    return ALLOWED_TRANSITIONS.has(`${from}->${to}`);
  };

  // Action visibility rules
  const locationValue = ((detail as any)?.itemLocation || '').toString();
  const hasStore = isBopis || locationValue.includes('Store') || storeServices.length > 0;
  const hasKerrville = locationValue.includes('Kerrville') || kerrvilleServices.length > 0 || orderClass === 'SPECIAL_ORDER';
  const isStoreOnly = hasStore && !hasKerrville;
  const isKerrvilleOnly = hasKerrville && !hasStore;
  const isCombinedOrder = hasStore && hasKerrville;

  const isProductCareOrder = isKerrvilleOnly || isCombinedOrder;
  const hasOpenProductCareService = kerrvilleServices.some(
  service => Number(service.status) === S.OPEN
);
  const canReadyForPickupOverride =
  canTransition(currentStatusId, S.READY_TO_PICKUP) ||
  (currentStatusId === S.OPEN && orderClass==="STANDARD" && isProductCareOrder && hasOpenProductCareService);
  
  const getServiceStatusId = (svc: any): StatusId | undefined => parseStatusId((svc as any)?.status);

  const storeServicesAllStatus = (target: StatusId, requireAtLeastOne = false) => {
    if (storeServices.length === 0) return !requireAtLeastOne;
    return storeServices.every((svc) => getServiceStatusId(svc) === target);
  };

  const kerrvilleServicesAllStatus = (target: StatusId, requireAtLeastOne = false) => {
    if (kerrvilleServices.length === 0) return !requireAtLeastOne;
    return kerrvilleServices.every((svc) => getServiceStatusId(svc) === target);
  };


  const storeServicesAllStatusOpen = storeServicesAllStatus(S.OPEN, true);
  const storeServicesAllStatusWorkInProgress = storeServicesAllStatus(S.WORK_IN_PROGRESS, true);
  const storeServicesAllStatusReadyToPickup = storeServicesAllStatus(S.READY_TO_PICKUP);
  const kerrvilleServicesAllStatusReadyToPickup = kerrvilleServicesAllStatus(S.READY_TO_PICKUP, true);

  const allServicesReadyForPickup = storeServicesAllStatusReadyToPickup && kerrvilleServicesAllStatus(S.READY_TO_PICKUP);

  const canStartBopis = isBopis
    && !isCancelledStatus
    && !isCompletedStatus
    && statusIs(S.OPEN)
    && canTransition(currentStatusId, S.WORK_IN_PROGRESS);
  const canFinishBopis = isBopis
    && !isCancelledStatus
    && !isCompletedStatus
    && statusIs(S.WORK_IN_PROGRESS)
    && canTransition(currentStatusId, S.READY_TO_PICKUP);
  const canCompleteBopis = isBopis
    && !isCancelledStatus
    && !isCompletedStatus
    && statusIs(S.READY_TO_PICKUP)
    && canTransition(currentStatusId, S.COMPLETED);
  const showCancel = !isCancelledStatus && canTransition(currentStatusId, S.CANCELLED);

  const finishInfoAlert = (
    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
      Finishing the service will mark this order Ready for Pickup.
    </Alert>
  );

  const bulkUpdateAlert = (
    <Alert severity="info" variant="outlined" sx={{ borderColor: 'pink' }}>
      <AlertTitle>Action Required</AlertTitle>
      This order must be processed through the Ship to Product Care procedure before services can be performed.
      <Box mt={1}>
        <Button size="small" variant="outlined" onClick={() => navigate('/bulk-tracking')}>
          Ship to Product Care
        </Button>
      </Box>
    </Alert>
  );

  const specialInProgressAlert = (
    <Alert severity="info" variant="outlined">
      <AlertTitle>In Progress</AlertTitle>
      Product Care is still processing this special order. Please check back after the status updates.
    </Alert>
  );

  const specialShippedAlert = (
    <Alert severity="info" variant="outlined">
      <AlertTitle>Shipped to Store</AlertTitle>
      Product Care has shipped this order to the store. Once received, scan or enter the delivery ID to continue.
      <Box mt={1}>
        <Button size="small" variant="outlined" onClick={() => navigate('/scan-delivery')}>
          Open Scan Delivery
        </Button>
      </Box>
    </Alert>
  );

  const cancelButton = showCancel ? (
    <LoadingButton
      variant="outlined"
      color="error"
      fullWidth
      onClick={() => {
        setCancelReason('');
        setCancelDialogOpen(true);
      }}
      startIcon={<BlockIcon />}
      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
    >
      Cancel Order
    </LoadingButton>
  ) : null;

  const holdCollectionButton = currentStatusId === S.ON_HOLD && canTransition(currentStatusId, S.COMPLETED)
    ? (
      <LoadingButton
        variant="contained"
        color="success"
        fullWidth
        onClick={() => setConfirmOpen(true)}
        loading={completing}
        loadingPosition="start"
        startIcon={<CheckCircleIcon />}
        disabled={actionSubmitting}
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
      >
        Mark as Collected
      </LoadingButton>
    )
    : null;

  const buildActionsStack = (items: React.ReactNode[], keyPrefix: string) => {
    const finalItems = [...items];
    if (holdCollectionButton) {
      finalItems.push(cloneElement(holdCollectionButton, { key: `${keyPrefix}-collect` }));
    }
    if (cancelButton) {
      finalItems.push(cloneElement(cancelButton, { key: `${keyPrefix}-cancel` }));
    }
    return finalItems.length ? <Stack spacing={1.25}>{finalItems}</Stack> : null;
  };

  const actionContent = (() => {
    if (isCompletedStatus || isCancelledStatus) return null;

    if (statusIs(S.ON_HOLD)) {
      return buildActionsStack([], 'hold');
    }

    if (orderClass === 'QUICK') return null;

    if (orderClass === 'BOPIS') {
      const elements: React.ReactNode[] = [];
      if (canStartBopis) {
        elements.push(
          <LoadingButton
            key="bopis-start"
            variant="contained"
            color="primary"
            fullWidth
            disabled={actionSubmitting}
            onClick={() => updateOrderStatus(S.WORK_IN_PROGRESS, 'Service started.', 'Store')}
            startIcon={<PlayCircleIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Start Service
          </LoadingButton>
        );
      }
      if (canFinishBopis) {
        elements.push(cloneElement(finishInfoAlert, { key: 'bopis-finish-info' }));
        elements.push(
          <LoadingButton
            key="bopis-finish"
            variant="outlined"
            color="success"
            fullWidth
            disabled={actionSubmitting}
            onClick={() => updateOrderStatus(S.READY_TO_PICKUP, 'Service finished and marked Ready for Pickup.', 'Store')}
            startIcon={<PauseCircleIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Finish Service
          </LoadingButton>
        );
      }
      if (canCompleteBopis) {
        elements.push(
          <LoadingButton
            key="bopis-complete"
            variant="contained"
            color="success"
            fullWidth
            onClick={() => setConfirmOpen(true)}
            loading={completing}
            loadingPosition="start"
            startIcon={<CheckCircleIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, py: 1.5, fontSize: '1rem' }}
          >
            Confirm Handover
          </LoadingButton>
        );
      }
      return buildActionsStack(elements, 'bopis');
    }

    if (orderClass === 'STANDARD') {
      if (isStoreOnly) {
        const elements: React.ReactNode[] = [];
        const canStart = statusIs(S.OPEN) && canTransition(currentStatusId, S.WORK_IN_PROGRESS);
        const canFinish = statusIs(S.WORK_IN_PROGRESS) && canTransition(currentStatusId, S.READY_TO_PICKUP);
        const canComplete = statusIs(S.READY_TO_PICKUP) && canTransition(currentStatusId, S.COMPLETED);

        if (canStart) {
          elements.push(
            <LoadingButton
              key="standard-store-start"
              variant="contained"
              color="primary"
              fullWidth
              disabled={actionSubmitting}
              onClick={() => updateOrderStatus(S.WORK_IN_PROGRESS, 'Service started.', 'Store')}
              startIcon={<PlayCircleIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
            >
              Start Service
            </LoadingButton>
          );
        }
        if (canFinish) {
          elements.push(cloneElement(finishInfoAlert, { key: 'standard-store-finish-info' }));
          elements.push(
            <LoadingButton
              key="standard-store-finish"
              variant="outlined"
              color="success"
              fullWidth
              disabled={actionSubmitting}
              onClick={() => updateOrderStatus(S.READY_TO_PICKUP, 'Service finished and marked Ready for Pickup.', 'Store')}
              startIcon={<PauseCircleIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
            >
              Finish Service
            </LoadingButton>
          );
        }
        if (canComplete) {
          elements.push(
            <LoadingButton
              key="standard-store-complete"
              variant="contained"
              color="success"
              fullWidth
              onClick={() => setConfirmOpen(true)}
              loading={completing}
              loadingPosition="start"
              startIcon={<CheckCircleIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, py: 1.5, fontSize: '1rem' }}
            >
              Confirm Handover
            </LoadingButton>
          );
        }
        return buildActionsStack(elements, 'standard-store');
      }

      if (isKerrvilleOnly) {
        const elements: React.ReactNode[] = [];
        if (statusIs(S.OPEN)) {
          elements.push(cloneElement(bulkUpdateAlert, { key: 'standard-product-care-bulk' }));
        }
        if (statusIs(S.READY_TO_PICKUP) && canTransition(currentStatusId, S.COMPLETED)) {
          elements.push(
            <LoadingButton
              key="standard-product-care-complete"
              variant="contained"
              color="success"
              fullWidth
              onClick={() => setConfirmOpen(true)}
              loading={completing}
              loadingPosition="start"
              startIcon={<CheckCircleIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, py: 1.5, fontSize: '1rem' }}
            >
              Confirm Handover
            </LoadingButton>
          );
        }
        return buildActionsStack(elements, 'standard-product-care');
      }

      if (isCombinedOrder) {
        const elements: React.ReactNode[] = [];
        if (statusIs(S.OPEN)) {
          if (storeServicesAllStatusOpen && kerrvilleServicesAllStatusReadyToPickup) {
            elements.push(
              <LoadingButton
                key="standard-combined-start"
                variant="contained"
                color="primary"
                fullWidth
                disabled={actionSubmitting}
                onClick={() => updateOrderStatus(S.WORK_IN_PROGRESS, 'Store service started.', 'Store')}
                startIcon={<PlayCircleIcon />}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
              >
                Start Store Service
              </LoadingButton>
            );
          } else {
            elements.push(cloneElement(bulkUpdateAlert, { key: 'standard-combined-bulk' }));
          }
        }
        if (
          statusIs(S.WORK_IN_PROGRESS)
          && storeServicesAllStatusWorkInProgress
          && kerrvilleServicesAllStatusReadyToPickup
          && canTransition(currentStatusId, S.READY_TO_PICKUP)
        ) {
          elements.push(cloneElement(finishInfoAlert, { key: 'standard-combined-finish-info' }));
          elements.push(
            <LoadingButton
              key="standard-combined-finish"
              variant="outlined"
              color="success"
              fullWidth
              disabled={actionSubmitting}
              onClick={() => updateOrderStatus(S.READY_TO_PICKUP, 'Store service marked Ready for Pickup.', 'Store')}
              startIcon={<PauseCircleIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
            >
              Finish Store Service
            </LoadingButton>
          );
        }
        if (statusIs(S.READY_TO_PICKUP) && allServicesReadyForPickup && canTransition(currentStatusId, S.COMPLETED)) {
          elements.push(
            <LoadingButton
              key="standard-combined-complete"
              variant="contained"
              color="success"
              fullWidth
              onClick={() => setConfirmOpen(true)}
              loading={completing}
              loadingPosition="start"
              startIcon={<CheckCircleIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, py: 1.5, fontSize: '1rem' }}
            >
              Confirm Handover
            </LoadingButton>
          );
        }
        return buildActionsStack(elements, 'standard-combined');
      }

      return buildActionsStack([], 'standard');
    }

    if (orderClass === 'SPECIAL_ORDER') {
      if (statusIs(S.READY_TO_PICKUP) && canTransition(currentStatusId, S.COMPLETED)) {
        const elements: React.ReactNode[] = [
          <LoadingButton
            key="special-complete"
            variant="contained"
            color="success"
            fullWidth
            onClick={() => setConfirmOpen(true)}
            loading={completing}
            loadingPosition="start"
            startIcon={<CheckCircleIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, py: 1.5, fontSize: '1rem' }}
          >
            Confirm Handover
          </LoadingButton>,
        ];
        return buildActionsStack(elements, 'special-complete');
      }

      if (statusIs(S.SHIPPED_TO_STORE_BY_KERRVILLE)) {
        return buildActionsStack([cloneElement(specialShippedAlert, { key: 'special-shipped' })], 'special-shipped');
      }

      return buildActionsStack([cloneElement(specialInProgressAlert, { key: 'special-progress' })], 'special-progress');
    }

    return buildActionsStack([], 'default');
  })();

  const orderSubtitle = useMemo(
    () => (detail?.orderExternalId ? `Order • ${detail.orderExternalId}` : 'Order Details'),
    [detail?.orderExternalId]
  );

  const associateInfo = useMemo(
    () => extractAssociateInfo(detail ?? summary ?? {}),
    [detail, summary]
  );

  const associateLabel = useMemo(() => {
    if (associateInfo.name) {
      return associateInfo.email ? `${associateInfo.name} (${associateInfo.email})` : associateInfo.name;
    }
    if (associateInfo.email) return associateInfo.email;
    if (associateInfo.id) return associateInfo.id;
    return null;
  }, [associateInfo.email, associateInfo.id, associateInfo.name]);

  const handoverAssociateInfo = useMemo(() => {
    const source: Record<string, any> = {
      associateName: detail?.closeByAssociateName ?? summary?.closeByAssociateName,
      associateId: detail?.closeByAssociateId ?? summary?.closeByAssociateId,
      associateEmail: detail?.closeByAssociateEmail ?? summary?.closeByAssociateEmail,
    };
    return extractAssociateInfo(source);
  }, [detail, summary]);

  const handoverAssociateLabel = useMemo(() => {
    if (handoverAssociateInfo.name) {
      return handoverAssociateInfo.email
        ? `${handoverAssociateInfo.name} (${handoverAssociateInfo.email})`
        : handoverAssociateInfo.name;
    }
    if (handoverAssociateInfo.email) return handoverAssociateInfo.email;

    if (handoverAssociateInfo.id) return handoverAssociateInfo.id;
    return null;
  }, [handoverAssociateInfo.email, handoverAssociateInfo.id, handoverAssociateInfo.name]);

  const resolvedAssociateName = useMemo(
    () => (associateName?.trim() || associateEmail || associateId),
    [associateId, associateEmail, associateName]
  );

  const handoverTimestamp = useMemo(
    () => deriveHandoverTimestamp(detail ?? undefined, parsedServices),
    [detail, parsedServices]
  );

  const handoverLabel = handoverTimestamp ? formatOrderDateTime(handoverTimestamp) : null;

  const load = async () => {
    if (!externalIdentity || !primaryId) {
      setError('Missing order details.');
      setLoading(false);
      return;
    }
    try {
      setLineLoading(true);
      const data = await getServiceRequestsByOrderId(externalIdentity, primaryId);
      setDetail(data);
      const servicesRaw = (data as any)?.services;
      if (Array.isArray(servicesRaw)) {
        setParsedServices(servicesRaw as ParsedService[]);
      } else if (typeof servicesRaw === 'string') {
        try {
          const parsed = JSON.parse(servicesRaw) as ParsedService[];
          setParsedServices(Array.isArray(parsed) ? parsed : []);
        } catch {
          setParsedServices([]);
        }
      } else {
        setParsedServices([]);
      }
    } catch (err: any) {
      logApiError('Fetching order details', err);
      setError(formatApiErrorMessage(err, 'Failed to fetch order details.'));
    } finally {
      setLoading(false);
      setLineLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [externalIdentity, primaryId]);

  const handleCompleted = async () => {
    if (!detail) return;
    setCompleting(true);
    const request: UpdateServiceRequest = {
      serviceItemId: detail.serviceItemId,
      status: S.COMPLETED,
      associateId,
      associateName: resolvedAssociateName,
      associateEmail,
    };
    try {
      const successMessage = isBopis
        ? 'Order marked as handover complete. Return to packing to finalise.'
        : 'Service marked as handover complete.';
      await performStatusUpdate(request, successMessage, 'All');
      // navigate back after a short delay so the user sees feedback
      setTimeout(() => navigate('/'), 400);
    } catch (err) {
      logApiError('Completing order', err);
      setSnack(formatApiErrorMessage(err, 'Unable to update status. Please try again.'));
    } finally {
      setCompleting(false);
      setConfirmOpen(false);
    }
  };

  const withSubmitting = async (fn: () => Promise<void>) => {
    try {
      setActionSubmitting(true);
      await fn();
      await load();
    } catch (err) {
      logApiError('Order action failed', err);
      setSnack(formatApiErrorMessage(err, 'Action failed. Please try again.'));
    } finally {
      setActionSubmitting(false);
    }
  };
  const startServiceMessages = [
    "Service started.",
    "Store service started."
  ];
  const readyForPickupMessages =[
    "Service finished and marked Ready for Pickup.",
    "Store service marked Ready for Pickup."
  ]
  const updateOrderStatus = (
    status: StatusId,
    message: string,
    targetLocation: 'Store' | 'Kerrville' | 'All'
  ) => {
    if (!detail) return;
    withSubmitting(async () => {
      if (!detail) return;
      await performStatusUpdate({
        serviceItemId: detail.serviceItemId,
        status,
        ...(startServiceMessages?.includes(message) && {
          requestType: "START",
        }),
        ...(readyForPickupMessages?.includes(message) && {
          requestType: "READY",
        }),
        associateId,
        associateName: resolvedAssociateName,
        associateEmail,
      }, message, targetLocation);
    });
  };

  // Centralized status update helper for flexibility and error handling
  const performStatusUpdate = async (
    req: UpdateServiceRequest,
    successMessage?: string,
    targetLocation?: 'Store' | 'Kerrville' | 'All'
  ) => {
    try {
      if (!req.associateEmail) {
        req = { ...req, associateEmail };
      }
      // augment with per-service updates based on target location
      const services = Array.isArray(parsedServices) ? parsedServices : [];
      const buildList = (loc: 'Store' | 'Kerrville' | 'All') => {
        const pick = services.filter((svc: any) => {
          const l = (svc?.location_type || '').toString();
          if (loc === 'All') return true;
          return l.includes(loc);
        });
        return pick
          .map((svc: any) => svc?.service_guid)
          .filter(Boolean)
          .map((guid: string) => ({ service_guid: guid, status: req.status }));
      };
      const list = targetLocation ? buildList(targetLocation) : buildList('All');
      if (list.length) req = { ...req, service: list };
      // alert(JSON.stringify(req));
      await updateStatus(externalIdentity, req);
      disableScrollRestore()
      if (successMessage) setSnack(successMessage);
    } catch (err) {
      logApiError('Status update failed', err);
      setSnack(formatApiErrorMessage(err, 'Unable to update status. Please try again.'));
      throw err;
    }
  };

  const handleHoldClick = () => {
    if (actionSubmitting) return;
    setMenuAnchor(null);
    if (isBopis) {
      setSnack('BOPIS orders cannot be put on hold.');
      return;
    }
    if (!canTransition(currentStatusId, S.ON_HOLD)) return;
    setHoldDialogOpen(true);
  };

  const handleReadyOverride = () => {
    if (actionSubmitting || !canReadyForPickupOverride) return;
    setMenuAnchor(null);
    setReadyOverrideDialogOpen(true);
  };

  const confirmReadyOverride = () => {
    if (!detail) return;
    setReadyOverrideDialogOpen(false);
    withSubmitting(async () => {
      if (!detail) return;
      await performStatusUpdate(
        {
          serviceItemId: detail.serviceItemId,
          status: S.READY_TO_PICKUP,
          associateId,
          associateName: resolvedAssociateName,
          associateEmail,
          requestType:"READY"
        },
        'Marked Ready for Pickup (override).',
        'Kerrville'
      );
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Box p={4}>
        <Alert severity="error" variant="outlined">
          <AlertTitle>Error</AlertTitle>
          {error || 'No data found'}
        </Alert>
      </Box>
    );
  }
  const isProductCareAndStore =
  rawLocation?.includes('Kerrville') &&
  rawLocation?.includes('Store');
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h6">{orderClass === 'QUICK' ? 'Quick Service' : orderClass === 'STANDARD' ? 'Standard Service' : orderClass === 'SPECIAL_ORDER' ? 'Special Order' : 'BOPIS-BOSS Order'}</Typography>
            {/* {detail?.serviceItemId && <Chip size="small" label={`#${detail.serviceItemId}`} />} */}
          </Stack>
        }
        subtitle={orderSubtitle}
        showBackButton
        onBack={() => navigate(-1)}
        sticky
        elevateOnScroll
        align="left"
        rightSlot={
          <>
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More actions">
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {showPhotoField && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setPhotoOpen(true);
                  }}
                >
                  <PhotoCameraBackOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
                  View Photo
                </MenuItem>
              )}
              <MenuItem
                disabled={actionSubmitting || !canReadyForPickupOverride || storeServicesAllStatusWorkInProgress}
                onClick={handleReadyOverride}
              >
                <DoneAllIcon fontSize="small" style={{ marginRight: 8 }} />
                Ready for Pickup (override)
              </MenuItem>
              <MenuItem
                disabled={actionSubmitting || orderClass === 'QUICK' || isBopis || !canTransition(currentStatusId, S.ON_HOLD)}
                onClick={handleHoldClick}
              >
                <HourglassBottomIcon fontSize="small" style={{ marginRight: 8 }} />
                Put on Hold
              </MenuItem>
              <Divider />
              <MenuItem
                disabled={actionSubmitting || !showCancel}
                onClick={() => {
                  setMenuAnchor(null);
                  setCancelReason('');
                  setCancelDialogOpen(true);
                }}
              >
                <BlockIcon fontSize="small" style={{ marginRight: 8 }} />
                Cancel Order
              </MenuItem>
            </Menu>
          </>
        }
      />

      {lineLoading && (
        <Box sx={{ px: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Summary / Hero card */}
      <Card
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2,
            py: 1.25,
            background:
              theme.palette.mode === 'light'
                ? alpha(theme.palette.primary.main, 0.06)
                : alpha(theme.palette.primary.main, 0.18),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          })}
        >
          <Avatar
            variant="rounded"
            sx={(theme) => ({
              width: 36,
              height: 36,
              borderRadius: 1.2,
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.main,
            })}
          >
            <ReceiptLongIcon fontSize="small" />
          </Avatar>
          <Typography variant="subtitle1" fontWeight={700}>
            {detail.orderExternalId}
          </Typography>
          {(() => {
            const label = orderStatusLabel;
            const lower = label.toLowerCase();
            const tone = (theme: any) => {
              if (lower.includes('cancel')) return { bg: theme.palette.error.main, fg: theme.palette.error.main };
              if (lower.includes('complete')) return { bg: theme.palette.success.main, fg: theme.palette.success.main };
              if (lower.includes('ready')) return { bg: theme.palette.success.main, fg: theme.palette.success.main };
              if (lower.includes('hold')) return { bg: theme.palette.warning.main, fg: theme.palette.warning.main };
              if (lower.includes('open')) return { bg: theme.palette.info.main, fg: theme.palette.info.main };
              if (lower.includes('progress')) return { bg: theme.palette.info.main, fg: theme.palette.info.main };
              if (lower.includes('kerrville')) return { bg: theme.palette.info.main, fg: theme.palette.info.main };
              return { bg: theme.palette.primary.main, fg: theme.palette.primary.main };
            };

            return (
              <Chip
                size="small"
                label={label}
                sx={(theme) => ({
                  ml: 'auto',
                  borderRadius: 1,
                  fontWeight: 600,
                  bgcolor: alpha(tone(theme).bg, 0.15),
                  color: tone(theme).fg,
                })}
              />
            );
          })()}
        </Box>

        <CardContent sx={{ pt: 1, pb: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip
              icon={orderClass === 'QUICK'
                ? <BoltOutlinedIcon />
                : orderClass === 'SPECIAL_ORDER'
                  ? <WorkspacePremiumOutlinedIcon />
                  : orderClass === 'BOPIS'
                    ? <ShoppingBagOutlinedIcon />
                    : <BuildCircleOutlinedIcon />}
              label={orderTypeLabel}
              sx={(theme) => {
                const color = orderClass === 'QUICK'
                  ? theme.palette.warning.main
                  : orderClass === 'SPECIAL_ORDER'
                    ? theme.palette.secondary.main
                    : orderClass === 'BOPIS'
                      ? theme.palette.success.main
                      : theme.palette.primary.main;
                return {
                  borderRadius: 2,
                  bgcolor: alpha(color, 0.12),
                  color,
                  '& .MuiChip-icon': { color },
                };
              }}
            />
            <Chip
              icon={locationLabel === 'Store' ? <StorefrontOutlinedIcon /> : <PlaceOutlinedIcon />}
              label={locationLabelDisplay || '—'}
              sx={(theme) => {
                let color = theme.palette.grey[600];
                if (locationLabel === 'Store') color = theme.palette.primary.main;
                else if (locationLabel === 'Kerrville') color = theme.palette.info.main;
                else if (locationLabel === 'Kerrville and Store') color = theme.palette.success.main;
                return {
                  borderRadius: 2,
                  bgcolor: alpha(color, 0.12),
                  color,
                  '& .MuiChip-icon': { color },
                };
              }}
            />
          </Stack>
          <Grid container spacing={1.25}>
            {/* SKU */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Inventory2OutlinedIcon fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    SKU
                  </Typography>
                </Stack>

                <Typography variant="body2" fontWeight={600}>
                  {detail.sku}
                </Typography>
              </Stack>
            </Grid>

            {/* Tag */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <TagIcon fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    Tag
                  </Typography>
                </Stack>

                <Typography variant="body2" fontWeight={600}>
                  {(() => {
                    const t = (detail as any)?.tag ?? '';
                    const str = String(t);
                    return str.length > 25
                      ? `${str.slice(0, 20)}...`
                      : (str || '—');
                  })()}
                </Typography>
              </Stack>
            </Grid>

            {/* Customer */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <PersonOutlineIcon fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    Customer
                  </Typography>
                </Stack>

                <Typography variant="body2" sx={{ textAlign: 'right' }}>
                  {(detail.customerName ?? '').toString().trim() ||
                    CUSTOMER_NAME_PLACEHOLDER}
                </Typography>
              </Stack>
            </Grid>

            {/* Email */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <EmailOutlinedIcon fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ textAlign: 'right' }}>
                  {detail.customerEmail}
                </Typography>
              </Stack>
            </Grid>

            {/* Contact Prefs */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="text.secondary">
                  Contact Preferences
                </Typography>

                <Stack
                  direction="row"
                  gap={0.75}
                  flexWrap="wrap"
                  justifyContent="flex-end"
                >
                  {detail.contactByEmail && (
                    <Chip size="small" label="Email" variant="outlined" />
                  )}

                  {detail.contactByPhone && (
                    <Chip size="small" label="Phone" variant="outlined" />
                  )}

                  {!detail.contactByEmail && !detail.contactByPhone && (
                    <Chip size="small" label="None" variant="outlined" />
                  )}
                </Stack>
              </Stack>
            </Grid>     
          </Grid>
          {/* service activity header */}
          <Box
            sx={(theme) => ({
              mt: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
            })}
          >
            {/* Header */}
            <Box
              onClick={() => setServiceActivityOpen((prev) => !prev)}
              sx={() => ({
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                bgcolor: 'rgb(194 174 174 / 40%)',
                userSelect: 'none',
              })}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <BadgeOutlinedIcon fontSize="small" />
                <Typography variant="body2" fontWeight={600}>
                  {serviceActivityOpen
                    ? 'Service Activity'
                    : 'View Service Activity'}
                </Typography>
              </Stack>

              {serviceActivityOpen ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </Box>

            <Collapse in={serviceActivityOpen}>
              <Divider />

              <Box
                sx={{
                  p: 1.5,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                  },
                  columnGap: 3,
                  rowGap: 2,
                }}
              >
                {/* activities here */}
                {/* order created */}
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <AccessTimeIcon
                    sx={{
                      fontSize: 18,
                      mt: 0.25,
                      color: 'text.secondary',
                    }}
                  />

                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Order Created
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}
                    >
                      {associateLabel ?? 'N/A'}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {formatOrderDateTime(detail.created)}
                    </Typography>
                  </Box>
                </Stack>

                {isProductCareAndStore ? (
                    <>
                      {/* shipped to PC by  */}
                      {detail?.shippedToPcAssociateName && (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <WarehouseOutlinedIcon
                            sx={{
                              fontSize: 18,
                              mt: 0.25,
                              color: 'text.secondary',
                            }}
                          />

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              Shipped To PC
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {detail?.shippedToPcAssociateName ?? '-'}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                              {formatOrderDateTime(detail?.shippedToPcDate)}
                            </Typography>
                          </Box>
                        </Stack>
                      )}
                      {/* service received by  */}
                      {detail?.receivedByAssociateName && (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <MoveToInboxOutlinedIcon
                            sx={{
                              fontSize: 18,
                              mt: 0.25,
                              color: 'text.secondary',
                            }}
                          />

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              Received
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {detail?.receivedByAssociateName ?? '—'}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                              {formatOrderDateTime(detail?.serviceReceived) ??
                                '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      )}

                      {/* instore service started by */}

                      {detail?.startByAssociateName && (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <PlayCircleOutlineIcon
                            sx={{
                              fontSize: 18,
                              mt: 0.25,
                              color: 'text.secondary',
                            }}
                          />

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              In-Store Service Started
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {detail?.startByAssociateName ?? '-'}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                              {formatOrderDateTime(detail?.serviceStarted)}
                            </Typography>
                          </Box>
                        </Stack>
                      )}

                      {/* service ready for pickup by  */}

                      {detail?.readyToPickupByAssociateName && (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <MoveToInboxOutlinedIcon
                            sx={{
                              fontSize: 18,
                              mt: 0.25,
                              color: 'text.secondary',
                            }}
                          />

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              Ready To Pickup
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {detail?.readyToPickupByAssociateName ?? '—'}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                              {formatOrderDateTime(detail?.serviceReadyToPickUp) ??
                                '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      )}
                    </>
                  ) : (
                    <>
                      {/* shipped to PC by */}

                      {detail?.shippedToPcAssociateName && (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <WarehouseOutlinedIcon
                            sx={{
                              fontSize: 18,
                              mt: 0.25,
                              color: 'text.secondary',
                            }}
                          />

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              Shipped To PC
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {detail?.shippedToPcAssociateName ?? '-'}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                              {formatOrderDateTime(detail?.shippedToPcDate)}
                            </Typography>
                          </Box>
                        </Stack>
                      )}

                      {/* service started by */}

                      {detail?.startByAssociateName && (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <PlayCircleOutlineIcon
                            sx={{
                              fontSize: 18,
                              mt: 0.25,
                              color: 'text.secondary',
                            }}
                          />

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              Service Started
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {detail?.startByAssociateName ?? '-'}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                              {formatOrderDateTime(detail?.serviceStarted)}
                            </Typography>
                          </Box>
                        </Stack>
                      )}

                      {/* service received by  */}

                      {detail?.receivedByAssociateName && (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <MoveToInboxOutlinedIcon
                            sx={{
                              fontSize: 18,
                              mt: 0.25,
                              color: 'text.secondary',
                            }}
                          />

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {isKerrvilleOnly ? "Received / Ready To Pickup" : "Received"}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {detail?.receivedByAssociateName ?? '—'}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                              {formatOrderDateTime(detail?.serviceReceived) ??
                                '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      )}

                      {/* service ready for pickup by  */}

                      {detail?.readyToPickupByAssociateName && (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <MoveToInboxOutlinedIcon
                            sx={{
                              fontSize: 18,
                              mt: 0.25,
                              color: 'text.secondary',
                            }}
                          />

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              Ready To Pickup
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {detail?.readyToPickupByAssociateName ?? '—'}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                              {formatOrderDateTime(detail?.serviceReadyToPickUp) ??
                                '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      )}
                    </>
                )}

                {/* handed over */}

                {handoverAssociateLabel && (
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <CheckCircleOutlineIcon
                      color="success"
                      sx={{
                        fontSize: 18,
                        mt: 0.25,
                      }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Handover Completed
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {handoverAssociateLabel}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        {handoverLabel ?? '—'}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Box>
            </Collapse>
          </Box>      


          {/* Comment */}
          {detail.comments && (
            <Box
              sx={(theme) => ({
                mt: 1,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
                background:
                  theme.palette.mode === 'light'
                    ? alpha(theme.palette.info.light, 0.15)
                    : alpha(theme.palette.info.dark, 0.35),
                p: 1,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              })}
            >
              <NotesOutlinedIcon fontSize="small" color="info" sx={{ mt: '2px' }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Comment
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
                  {detail.comments}
                </Typography>
              </Box>
            </Box>
          )}


          {(() => {
            const trackingReference = (detail as any)?.trackingReference as string | undefined;
            const deliveryIdVal = (detail as any)?.deliveryId as string | undefined | null;
            const cancellationReasonCode = (detail as any)?.cancellationReasonCode as string | undefined;
            const holdCommentText = (detail as any)?.holdComment || (detail as any)?.hold_comment;
            const cancelCommentText = (detail as any)?.cancelComment || (detail as any)?.cancel_comment;

            const showTracking = !!trackingReference;
            const showDeliveryId = !!deliveryIdVal;
            const showCancelReason = isCancelledStatus && !!cancellationReasonCode;
            const showHoldComment = currentStatusId === S.ON_HOLD && !!holdCommentText;
            const showCancelComment = isCancelledStatus && !!cancelCommentText;
            const shouldShow = showTracking || showDeliveryId || showCancelReason || showHoldComment || showCancelComment;
            if (!shouldShow) return null;
            return (
              <Paper
                elevation={0}
                sx={(theme) => ({
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                  background: alpha(theme.palette.primary.light, 0.05),
                })}
              >
                <Typography variant="subtitle2" gutterBottom fontWeight={700}>
                  Additional Info
                </Typography>
                <Stack spacing={0.75}>
                  {showTracking && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">Tracking Reference</Typography>
                      <Typography variant="body2" fontWeight={500}>{trackingReference}</Typography>
                    </Stack>
                  )}
                  {showDeliveryId && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">Delivery ID</Typography>
                      <Typography variant="body2" fontWeight={500}>{deliveryIdVal}</Typography>
                    </Stack>
                  )}
                  {showCancelReason && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">Cancellation Reason</Typography>
                      <Typography variant="body2" fontWeight={500}>{cancellationReasonCode}</Typography>
                    </Stack>
                  )}
                  {showHoldComment && (
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="body2" color="text.secondary">Hold Comment</Typography>
                      <Typography variant="body2" sx={{ textAlign: 'right' }}>{String(holdCommentText)}</Typography>
                    </Stack>
                  )}
                  {showCancelComment && (
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="body2" color="text.secondary">Cancel Comment</Typography>
                      <Typography variant="body2" sx={{ textAlign: 'right' }}>{String(cancelCommentText)}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            );
          })()}
        </CardContent>
      </Card>

      {/* Services list */}
      <Card
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2,
            py: 1.25,
            background:
              theme.palette.mode === 'light'
                ? alpha(theme.palette.primary.main, 0.06)
                : alpha(theme.palette.primary.main, 0.18),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          })}
        >
          <Avatar
            variant="rounded"
            sx={(theme) => ({
              width: 32,
              height: 32,
              borderRadius: 1.2,
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.main,
            })}
          >
            {isBopis ? <AttachFileOutlinedIcon fontSize="small" /> : <Inventory2OutlinedIcon fontSize="small" />}
          </Avatar>
          <Typography variant="subtitle1" fontWeight={700}>
            {isBopis ? 'Attachments' : 'Services'}
          </Typography>
        </Box>

        <CardContent sx={{ py: 2 }}>
          {parsedServices.length > 0 ? (
            isBopis ? (
              <Stack spacing={1.25}>
                {parsedServices.map((service, index) => {
                  const sku = service?.service_sku || `Attachment ${index + 1}`;
                  const infoEntries = buildServiceInfoEntries(service as any, [1, 2, 3, 4]);
                  const additionalDetails = renderAdditionalDetails(
                    infoEntries,
                    `${service?.service_sku ?? sku}-${index}-bopis`,
                    detail?.sku,
                    isBopis
                  );

                  return (
                    <Paper
                      key={`${service.service_sku}-${index}`}
                      variant="outlined"
                      sx={(theme) => ({
                        p: 1.25,
                        borderRadius: 2,
                        borderColor: alpha(theme.palette.divider, 0.9),
                      })}
                    >
                      <Stack spacing={0.75}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Service SKU
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {sku}
                          </Typography>
                        </Stack>
                        {additionalDetails ?? (
                          <Typography variant="body2" color="text.secondary">
                            No additional details provided.
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              <Stack spacing={1.25}>
                {parsedServices.map((service, index) => {
                  const rawName = (service as any)?.service_description ?? (service as any)?.description ?? '';
                  const serviceName = String(rawName || '').trim();
                  const infoEntries = buildServiceInfoEntries(service as any, [1, 2, 3, 4]);
                  const additionalDetails = renderAdditionalDetails(
                    infoEntries,
                    `${service?.service_sku ?? 'service'}-${index}`,
                    detail?.sku,
                    isBopis
                  );
                  return (
                    <Paper
                      key={`${service.service_sku}-${index}`}
                      variant="outlined"
                      sx={(theme) => ({
                        p: 1.25,
                        borderRadius: 2,
                        borderColor: alpha(theme.palette.divider, 0.9),
                      })}
                    >
                      <Stack spacing={1.25}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="body2" color="text.secondary">
                            Service
                          </Typography>
                          <Stack spacing={0.5} alignItems="flex-end">
                            {serviceName && (
                              <Typography variant="body1" fontWeight={600} sx={{ textAlign: 'right' }}>
                                {serviceName}
                              </Typography>
                            )}
                            <Chip
                              size="small"
                              label={service.service_sku}
                              variant={serviceName ? 'outlined' : 'filled'}
                              sx={(theme) => ({
                                fontWeight: 600,
                                borderRadius: 1,
                                borderColor: alpha(theme.palette.primary.main, 0.4),
                                color: serviceName ? theme.palette.text.secondary : theme.palette.primary.contrastText,
                                bgcolor: serviceName ? 'transparent' : alpha(theme.palette.primary.main, 0.75),
                              })}
                            />
                          </Stack>
                        </Stack>

                        {((service as any)?.location_type) && (
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Location</Typography>
                            {(() => {
                              const loc = ((service as any)?.location_type || '').toString();
                              const labelInner = loc.includes('Kerrville') && loc.includes('Store') ? 'Kerrville and Store' : loc || '—';
                              const displayLabel = labelInner
                                .replace(/Kerrville/g, 'Product Care')
                                .replace(/Store/g, 'In Store');
                              const icon = labelInner === 'Store' ? <StorefrontOutlinedIcon /> : <PlaceOutlinedIcon />;
                              return (
                                <Chip
                                  size="small"
                                  icon={icon}
                                  label={displayLabel}
                                  sx={(theme) => {
                                    let color = theme.palette.grey[600];
                                    if (labelInner === 'Store') color = theme.palette.primary.main;
                                    else if (labelInner === 'Kerrville') color = theme.palette.info.main;
                                    else if (labelInner === 'Kerrville and Store') color = theme.palette.success.main;
                                    return {
                                      borderRadius: 2,
                                      bgcolor: alpha(color, 0.12),
                                      color,
                                      '& .MuiChip-icon': { color },
                                    };
                                  }}
                                />
                              );
                            })()}
                          </Stack>
                        )}

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Status</Typography>
                          {(() => {
                            const rawStatusId = (service as any).status as string | number | undefined;
                            const labelInner = getConsolidatedStatusLabel(rawStatusId);
                            const lower = labelInner.toLowerCase();
                            const tone = (theme: any) => {
                              if (lower.includes('cancel')) return { bg: theme.palette.error.main, fg: theme.palette.error.main };
                              if (lower.includes('complete')) return { bg: theme.palette.success.main, fg: theme.palette.success.main };
                              if (lower.includes('ready')) return { bg: theme.palette.success.main, fg: theme.palette.success.main };
                              if (lower.includes('hold')) return { bg: theme.palette.warning.main, fg: theme.palette.warning.main };
                              if (lower.includes('open')) return { bg: theme.palette.info.main, fg: theme.palette.info.main };
                              if (lower.includes('progress')) return { bg: theme.palette.info.main, fg: theme.palette.info.main };
                              if (lower.includes('kerrville')) return { bg: theme.palette.info.main, fg: theme.palette.info.main };
                              return { bg: theme.palette.primary.main, fg: theme.palette.primary.main };
                            };
                            return (
                              <Chip
                                size="small"
                                label={labelInner}
                                sx={(theme) => ({
                                  borderRadius: 1,
                                  fontWeight: 600,
                                  bgcolor: alpha(tone(theme).bg, 0.15),
                                  color: tone(theme).fg,
                                })}
                              />
                            );
                          })()}
                        </Stack>

                        {additionalDetails}
                        {service.service_note && (
                          <Box
                            sx={(theme) => ({
                              mt: 1,
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
                              background:
                                theme.palette.mode === 'light'
                                  ? alpha(theme.palette.info.light, 0.15)
                                  : alpha(theme.palette.info.dark, 0.35),
                              p: 1,
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                            })}
                          >
                            <NotesOutlinedIcon fontSize="small" color="info" sx={{ mt: '2px' }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Service Note
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
                                {service.service_note}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )
          ) : (
            <Alert severity="info" variant="outlined">
              {isBopis ? 'This order does not have any attachments yet.' : 'This order does not have any services yet.'}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Photo section (thumbnail) */}
      {showPhotoField && (
        <Card
          sx={(theme) => ({
            mx: 2,
            mt: 2,
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          })}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 2,
              py: 1.25,
              background:
                theme.palette.mode === 'light'
                  ? alpha(theme.palette.primary.main, 0.06)
                  : alpha(theme.palette.primary.main, 0.18),
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            })}
          >
            <Avatar
              variant="rounded"
              sx={(theme) => ({
                width: 32,
                height: 32,
                borderRadius: 1.2,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
              })}
            >
              <PhotoCameraBackOutlinedIcon fontSize="small" />
            </Avatar>
            <Typography variant="subtitle1" fontWeight={700}>
              Photo
            </Typography>
          </Box>

          <CardContent>
            <Paper
              variant="outlined"
              onClick={() => setPhotoOpen(true)}
              sx={{
                maxWidth: 220,
                cursor: 'zoom-in',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <img
                src={detail.photoUrl}
                alt="Uploaded"
                style={{ maxWidth: 220, display: 'block' }}
              />
            </Paper>
          </CardContent>
        </Card>
      )}

      {/* bottom actions (vary by order type) */}
      <Box mx={2} mt={3} mb={4}>
        {actionContent}
      </Box>

      {/* confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm handover?</DialogTitle>
        <DialogContent>
          {isBopis ? (
            <DialogContentText>
              This will mark the order as <b>Handover Complete</b> and finish the pickup flow.
            </DialogContentText>
          ) : (
            <DialogContentText>
              This will mark the service as <b>Handover Complete</b> and notify relevant systems.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={handleCompleted}
            loading={completing}
            variant="contained"
            startIcon={<CheckCircleIcon />}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Ready for Pickup override confirmation */}
      <Dialog
        open={readyOverrideDialogOpen}
        onClose={() => setReadyOverrideDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Override to Ready for Pickup?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will immediately mark all Product Care services on this order as <b>Ready for Pickup</b> and notify the store team. Only use this override when you are certain all Product Care work is complete.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReadyOverrideDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={confirmReadyOverride}
            loading={actionSubmitting}
            startIcon={<DoneAllIcon />}
          >
            Confirm Override
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* photo lightbox */}
      {showPhotoField && (
        <Dialog open={photoOpen} onClose={() => setPhotoOpen(false)} maxWidth="md" fullWidth>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ py: 1 }}>
              Photo
            </Typography>
            <IconButton onClick={() => setPhotoOpen(false)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>
          <DialogContent dividers>
            <Box sx={{ width: '100%', textAlign: 'center' }}>
              <img
                src={detail.photoUrl!}
                alt="Uploaded"
                style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
              />
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* Put on Hold dialog (with comments) */}
      <Dialog open={holdDialogOpen} onClose={() => setHoldDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Put on Hold</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add a note for putting this order on hold.
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            minRows={3}
            fullWidth
            value={holdComment}
            onChange={(e) => setHoldComment(e.target.value)}
            placeholder="Reason / comments"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHoldDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            disabled={actionSubmitting}
            onClick={() =>
              withSubmitting(async () => {
                if (!holdComment || !holdComment.trim()) {
                  setSnack('Please enter a comment to put on hold.');
                  return;
                }
                if (!detail) return;
                await performStatusUpdate({
                  serviceItemId: detail.serviceItemId,
                  status: S.ON_HOLD,
                  associateId,
                  associateName: resolvedAssociateName,
                  associateEmail,
                  hold_comment: holdComment.trim(),
                }, 'Order put on hold.', 'All');
                setHoldDialogOpen(false);
                setHoldComment('');
              })
            }
          >
            Confirm Hold
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delivery ID dialog for Product Care flows */}
      <Dialog open={deliveryDialogOpen} onClose={() => setDeliveryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enter Delivery ID</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Scan or manually enter the delivery ID to mark as received and ready for pickup.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            value={deliveryId}
            onChange={(e) => setDeliveryId(e.target.value)}
            placeholder="Delivery ID"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveryDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            disabled={actionSubmitting || !deliveryId}
            onClick={() =>
              withSubmitting(async () => {
                if (!detail || !deliveryId) return;
                await performStatusUpdate({
                  serviceItemId: detail.serviceItemId,
                  status: S.READY_TO_PICKUP,
                  associateId,
                  associateName: resolvedAssociateName,
                  associateEmail,
                  deliveryId,
                }, 'Received. Ready for Pickup.', 'Kerrville');
                setDeliveryDialogOpen(false);
              })
            }
            startIcon={<DoneAllIcon />}
          >
            Save & Mark Received
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Cancel dialog with reason + comment */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <FormControl>
              <FormLabel id="cancel-reason-label">Reason</FormLabel>
              <RadioGroup
                aria-labelledby="cancel-reason-label"
                name="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value as 'ADMIN_ERROR' | 'NO_PICKUP')}
              >
                <FormControlLabel
                  value="ADMIN_ERROR"
                  control={<Radio />}
                  label="Admin Error"
                  disabled={currentStatusId !== undefined && currentStatusId >= S.SHIPPED_TO_KERRVILLE}
                />
                <FormControlLabel value="NO_PICKUP" control={<Radio />} label="No Pickup" />
              </RadioGroup>
            </FormControl>
            <TextField
              label="Comment"
              multiline
              minRows={3}
              fullWidth
              value={cancelComment}
              onChange={(e) => setCancelComment(e.target.value)}
              placeholder="Add cancellation details"
              required
              helperText="Comment is required"
            />
            {!isCs1Store && (
              <Alert severity="warning" variant="outlined">
                Confirm if a refund is due to Guest and process a return separately.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Close</Button>
          <LoadingButton
            variant="contained"
            color="error"
            disabled={actionSubmitting || !cancelComment.trim()}
            onClick={() =>
              withSubmitting(async () => {
                if (!cancelReason) {
                  setSnack('Select a reason to cancel.');
                  return;
                }
                // allow cancel whenever transition is allowed (e.g., Open, Ready, Hold)
                if (!canTransition(currentStatusId, S.CANCELLED)) {
                  setSnack('Cancel not allowed for current state.');
                  return;
                }
                if (!detail) return;
                const trimmedComment = cancelComment.trim();
                if (!trimmedComment) {
                  setSnack('Please enter a cancellation comment.');
                  return;
                }
                await performStatusUpdate({
                  serviceItemId: detail.serviceItemId,
                  status: S.CANCELLED,
                  associateId,
                  associateName: resolvedAssociateName,
                  associateEmail,
                  reasonCode: cancelReason,
                  cancel_comment: trimmedComment,
                }, 'Order cancelled.', 'All');
                setCancelDialogOpen(false);
                setCancelComment('');
                setSnack(
                  isCs1Store
                    ? `Order cancelled (${cancelReason === 'ADMIN_ERROR' ? 'Admin Error' : 'No Pickup'}).`
                    : 'Cancellation complete. Confirm if a refund is due to Guest and process a return separately.'
                );
                alert(isCs1Store ? 'Order cancelled.' : 'Cancellation complete. Confirm if a refund is due to Guest and process a return separately.');
              })
            }
            startIcon={<BlockIcon />}
          >
            Confirm Cancel
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* snack feedback */}
      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
      />

      {/* overlay loader during submissions */}
      <Backdrop
        open={actionSubmitting || completing}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Stack alignItems="center" spacing={1}>
          <CircularProgress color="inherit" />
          <Typography variant="body2">Submitting…</Typography>
        </Stack>
      </Backdrop>
    </Box>
  );
};

export default StoreViewOrderDetailsPage;
type ValueLine = { label?: string; value: string };

const friendlyKey = (key: string) => toTitle(key.replace(/[_-]+/g, ' '));

const formatPrimitive = (value: any): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value, null, 2);
};

const parseServiceInfoValue = (raw: unknown): ValueLine[] => {
  if (raw == null) return [];

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return Object.entries(raw as Record<string, unknown>).map(([key, value]) => ({
      label: friendlyKey(key),
      value: formatPrimitive(value).trim(),
    }));
  }

  if (Array.isArray(raw)) {
    if (!raw.length) return [];
    return raw.map((value, index) => {
      const formatted = formatPrimitive(value).trim();
      return {
        label: raw.length > 1 ? `Item ${index + 1}` : undefined,
        value: formatted,
      };
    });
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        return parseServiceInfoValue(parsed);
      } catch {
        // ignore parsing failure, fall back to raw text
      }
    }
    return [{ value: trimmed }];
  }

  return [{ value: formatPrimitive(raw).trim() }];
};

const buildServiceInfoEntries = (
  service: Record<string, any>,
  slots: number[],
): Array<{ label: string; lines: ValueLine[] }> =>
  slots
    .map((slot) => {
      const infoKey = service?.[`service_info_${slot}`];
      const valueRaw = service?.[`service_value_${slot}`];
      const lines = parseServiceInfoValue(valueRaw);
      if ((!infoKey || !String(infoKey).trim()) && lines.length === 0) return null;
      const label = infoKey ? friendlyKey(String(infoKey)) : `Detail ${slot}`;
      return { label, lines };
    })
    .filter((entry): entry is { label: string; lines: ValueLine[] } => Boolean(entry));

const formatDetailValue = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '—';
  if (/^true$/i.test(trimmed)) return 'Yes';
  if (/^false$/i.test(trimmed)) return 'No';
  return trimmed;
};

const renderAdditionalDetails = (
  entries: Array<{ label: string; lines: ValueLine[] }>,
  keyPrefix: string,
  detailSku: string,
  isBopis: boolean
) => {
  if (!entries.length) return null;
  const orderedEntries = reorderEntriesWithSkuFirst(entries)
  return (
    <Stack spacing={1} sx={{ mt: 0.75 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.4 }}
      >
        Additional Details
      </Typography>
      <Stack spacing={1}>
        {orderedEntries.map((entry, entryIndex) => {
          const isEngravingText = /engraving text/i.test(entry.label);
          const isRingSize = /ring size/i.test(entry.label);
          const primaryLine = entry.lines[0];
          const secondaryLines = entry.lines.slice(primaryLine ? 1 : 0);

          return (
            <Box
              key={`${keyPrefix}-${entryIndex}`}
              sx={(theme) => ({
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                background:
                  theme.palette.mode === 'light'
                    ? alpha(theme.palette.primary.light, 0.12)
                    : alpha(theme.palette.primary.dark, 0.35),
                p: 1.25,
              })}
            >
              <Stack direction="row" spacing={4} flexWrap="wrap" alignItems="center">
              { isBopis && !detailSku?.includes("KIT") && (
              <Stack>
              <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
                Attach
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                {detailSku}
              </Typography>
              </Stack>
              )
             } 
              <Stack>
              <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
                {entry.label}
              </Typography>

              {isEngravingText && primaryLine && (
                <Box
                  sx={(theme) => ({
                    mt: 1,
                    borderRadius: 2,
                    border: `1px dashed ${alpha(theme.palette.secondary.main, 0.4)}`,
                    background:
                      theme.palette.mode === 'light'
                        ? alpha(theme.palette.secondary.light, 0.16)
                        : alpha(theme.palette.secondary.dark, 0.4),
                    p: 1.25,
                  })}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <FormatQuoteOutlinedIcon fontSize="small" color="secondary" />
                    <Typography variant="subtitle1" sx={{ fontStyle: 'italic', fontWeight: 600 }}>
                      &ldquo;{formatDetailValue(primaryLine.value ?? '')}&rdquo;
                    </Typography>
                  </Stack>
                </Box>
              )}

              {isRingSize && primaryLine && (
                <Box
                  sx={(theme) => ({
                    mt: 1,
                    borderRadius: 2,
                    border: `1px dashed ${alpha(theme.palette.secondary.main, 0.4)}`,
                    background:
                      theme.palette.mode === 'light'
                        ? alpha(theme.palette.secondary.light, 0.18)
                        : alpha(theme.palette.secondary.dark, 0.35),
                    p: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                  })}
                >
                  <Typography variant="subtitle2" color="secondary.main" sx={{ fontWeight: 700 }}>
                    Ring Size
                  </Typography>
                  <Chip
                    size="small"
                    color="secondary"
                    label={formatDetailValue(primaryLine.value ?? '')}
                    sx={{ fontWeight: 700, borderRadius: 1.5 }}
                  />
                </Box>
              )}

              {!isEngravingText && !isRingSize && primaryLine && (
                <Typography
                  variant="body2"
                  sx={{ mt: 0.75, whiteSpace: 'pre-wrap', fontWeight: 500 }}
                >
                  {formatDetailValue(primaryLine.value ?? '')}
                </Typography>
              )}

              {secondaryLines.length > 0 ? (
                <Stack spacing={0.6} sx={{ mt: 0.75 }}>
                  {secondaryLines.map((line: any, lineIndex: any) => (
                    <Stack
                      key={`${keyPrefix}-${entryIndex}-row-${lineIndex}`}
                      direction="row"
                      spacing={1}
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ gap: 1 }}
                    >
                      {line.label ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {line.label}
                        </Typography>
                      ) : null}
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          fontWeight: 500,
                          textAlign: line.label ? 'right' : 'left',
                          flex: 1,
                          ml: line.label ? 2 : 0,
                        }}
                      >
                        {formatDetailValue(line.value ?? '')}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : null}

              {!primaryLine && secondaryLines.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  —
                </Typography>
              )}
              </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};
